<template>
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white dark:bg-dark-900 outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{
          message.header
          }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button label="Save" @click="acceptCallback" class="w-32"></Button>
          <Button label="Cancel" outlined @click="rejectCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Transaksi Baru" />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Marketing Selection -->
        <InputSelect v-model="selectedMarketingId" label="Marketing (opsional)" id="pilih-marketing"
          :options="optionMarketing" placeholder="Pilih marketing" :virtual-scroller-options="{ itemSize: 38 }"
          :is-scrolled="allMarketings.length > 10" :valid="validation.marketing_id.valid"
          :error-message="validation.marketing_id.message" />

        <!-- Customer Name -->
        <InputTextCustom v-model="customerName" id="nama-pelanggan" label="Nama Pelanggan (opsional)"
          placeholder="Masukkan nama pelanggan" div_class="my-1" :valid="validation.name.valid"
          :error-message="validation.name.message" />
      </div>

      <!-- Order Type Selection -->
      <div class="mt-4">
        <label class="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2 block">
          Jenis Pesanan
        </label>
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div v-for="type in orderTypes" :key="type.id" class="relative" @click="selectOrderType(type)">
            <input type="radio" :name="type.code" :id="type.code" :value="type.code" v-model="selectedOrderType"
              class="hidden peer" />
            <label :for="type.code"
              class="block p-4 text-center rounded-lg border-2 cursor-pointer transition-all duration-200 peer-checked:border-primary-700 peer-checked:text-white peer-checked:bg-primary-500 hover:border-primary-500 dark:border-dark-500 dark:peer-checked:border-primary-800 dark:hover:border-primary-500 bg-white dark:bg-dark-800 dark:text-dark-200 uppercase font-bold tracking-wider">
              {{ type.name }}
            </label>
          </div>
        </div>
      </div>

      <!-- Table Selection (shown only for dine-in) -->
      <div v-if="selectedOrderType === 'dine_in'" class="mt-8 pb-24">
        <NewTransactionCardTable :tables="allTables" :selectedTableId="selectedTableId"
          @table-selected="handleTableSelected" />
      </div>

      <!-- Action Button -->
      <div
        class="fixed bottom-0 right-0 w-full bg-white dark:bg-dark-900 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] p-4 space-y-4 z-50 flex justify-center">
        <button
          class="w-full max-w-xl px-6 py-2 bg-primary-600 text-white rounded-lg border-2 border:primary-700 dark:border-primary-700 hover:bg-primary-500 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider font-bold"
          :disabled="!isFormValid" @click="createOrder">
          {{
            !relevantCashRegister
              ? "Tidak ada Cash Register yang aktif"
              : "Buat Transaksi"
          }}
        </button>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import NewTransactionCardTable from "@/components/card-dashboard/card-table/NewTransactionCardTable.vue";
import { mapGetters, mapActions } from "vuex";
import { ConfirmDialog } from "primevue";
import InputSelect from "@/components/form/InputSelect.vue";
import InputTextCustom from "@/components/form/InputTextCustom.vue";

export default {
  name: "NewTransactionView",
  components: {
    IndexView,
    PageTitle,
    ViewContainer,
    NewTransactionCardTable,
    ConfirmDialog,
    InputSelect,
    InputTextCustom,
  },
  data() {
    return {
      isBlur: false,
      selectedMarketingId: null,
      customerName: "",
      selectedOrderType: null,
      selectedTableId: null,
      canReserve: ["available", "ready"],
      orderTypes: [
        {
          id: 1,
          code: "dine_in",
          name: "Dine In",
          need_table: true,
          close_bill: false,
          is_service_charge: true,
          is_tax: true,
          is_discount: true,
        },
        {
          id: 2,
          code: "take_away",
          name: "Take Away",
          need_table: false,
          close_bill: false,
          is_service_charge: true,
          is_tax: true,
          is_discount: true,
        },
        {
          id: 3,
          code: "staff",
          name: "Staff",
          need_table: false,
          close_bill: true,
          is_service_charge: false,
          is_tax: false,
          is_discount: false,
        },
      ],
      validation: {
        name: {
          valid: true,
          message: "",
        },
        marketing_id: {
          valid: true,
          message: "",
        },
      },
    };
  },
  computed: {
    ...mapGetters("marketings", ["allMarketings"]),
    ...mapGetters("tables", ["allTables"]),
    ...mapGetters("cash_registers", ["allCashRegisters"]),
    relevantCashRegister() {
      return this.allCashRegisters.find((cr) => cr.status === "open");
    },
    optionMarketing() {
      let options = [];
      options.push({ value: null, label: "Pilih marketing" });
      options = options.concat(this.allMarketings.map((marketing) => ({
        value: marketing.id,
        label: marketing.name,
      })))
      return options;
    },
    isFormValid() {
      if (!this.relevantCashRegister) return false;
      if (!this.selectedOrderType) return false;
      if (this.selectedOrderType === "dine_in" && !this.selectedTableId)
        return false;
      return true;
    },
  },
  created() {
    this.fetchMarketings({ limit: 100 });
    this.fetchTables({ limit: 100 });
    this.fetchCashRegisters();

    // Handle pre-filled values from query parameters
    const { tableId, orderType } = this.$route.query;
    console.log("new-transaction", this.$route.query);

    if (orderType) {
      this.selectedOrderType = orderType;
    }

    if (tableId) {
      this.selectedTableId = parseInt(tableId);
    }
  },
  methods: {
    ...mapActions("marketings", ["fetchMarketings"]),
    ...mapActions("tables", ["fetchTables", "updateTable"]),
    ...mapActions("cash_registers", ["fetchCashRegisters"]),
    ...mapActions("sales_orders", ["createSalesOrder"]),
    selectOrderType(type) {
      this.selectedOrderType = type.code;
      if (!type.need_table) {
        this.selectedTableId = 1;
      }
    },
    handleTableSelected(table) {
      if (this.canReserve.includes(table.status)) {
        this.selectedTableId = table.id;
      }
    },
    async createOrder() {
      if (!this.isFormValid) {
        return;
      }

      try {
        const response = await this.createSalesOrder({
          table_id: this.orderTypes.find(
            (type) => type.code === this.selectedOrderType
          )?.need_table
            ? this.selectedTableId
            : null,
          marketing_id: this.selectedMarketingId,
          cash_register_id: this.relevantCashRegister.id,
          transaction_type_id: 1,
          customer_name: this.customerName,
          customer_phone: "",
          sub_total: 0,
          grand_total: 0,
          status: "pending",
          order_type: this.selectedOrderType,
          payment_amount: 0,
          payment_change: 0,
          tax: 0,
          service_charge: 0,
          discount: 0,
          note: "",
        });

        const orderId = response.data.id;
        if (
          this.orderTypes.find((type) => type.code === this.selectedOrderType)
            ?.need_table
        ) {
          await this.updateTable({
            id: this.selectedTableId,
            status: "unavailable",
          });
        }

        this.$router.push({
          name: "order",
          query: {
            tableId: this.selectedTableId,
            customerName: this.customerName,
            marketingId: this.selectedMarketingId,
            orderType: this.selectedOrderType,
            orderId,
            cashRegisterId: this.relevantCashRegister.id,
            new: false,
          },
        });
      } catch (error) {
        console.error("Error processing order:", error);
        alert("Terjadi kesalahan saat memproses pesanan");

        if (this.selectedTableId) {
          try {
            await this.updateTable({
              id: this.selectedTableId,
              status: "available",
            });
          } catch (revertError) {
            console.error("Error reverting table status:", revertError);
          }
        }
      }
    },
  },
};
</script>
